import React, { useEffect, useState } from 'react';
import { sharedTableStyles } from '../../utils/tableStyles';
import { TableSkeleton } from '../../components/UI/Skeleton/Skeleton';
import RowActionMenu from '../../components/UI/RowActionMenu/RowActionMenu';
import TableCard from '../../components/UI/TableToolbar/TableCard';
import TableToolbar from '../../components/UI/TableToolbar/TableToolbar';
import { Modal } from 'react-bootstrap';
import _debounce from 'lodash/debounce';
import { useForm, Controller } from 'react-hook-form';
import DataTable from 'react-data-table-component';
import { yupResolver } from '@hookform/resolvers/yup';
import Select from 'react-select';

import { useIsLoggedInQuery } from '../../redux/apis/authApi';
import {
  useAddHolidayMutation,
  useDeleteHolidayMutation,
  useEditHolidayMutation,
  useGetHolidaysQuery
} from '../../redux/apis/holidayApi';

import EmptyData from '../../components/UI/EmptyData';
import PageHeader from '../../components/common/PageHeader';
import { HolidaysData } from '../../components/Data/AppData';
import DeleteModel from '../../components/common/DeleteModel';

import { GLOBAL, HOLIDAY_LOCALITY_OPTIONS, getRecordLocality } from '../../utils/constant/global';
import {
  getErrorMessage,
  formatDate,
  editDateFormate,
  paginationRow,
  skipRow
} from '../../utils/handler';
import { notification } from '../../utils/notification';
import { holidaySchema } from '../../utils/schemas/auth';
import { TOAST_TYPE } from '../../utils/constant/errorMessage';
import { BT_BUTTON_LABELS } from '../../utils/constant/buttons';
import { INPUT_LABEL, PLACEHOLDER } from '../../utils/constant/placeHolders';
import { ROLES } from '../../utils/constant/global';
import './Holidays.scss';
import {
  LB_HEADER,
  LB_HOLIDAY_TABLE_COLS,
  LB_HOLIDAY_TABLE_COL_SORT
} from '../../utils/constant/labels';

const { LB_HOLIDAY } = LB_HEADER;
const { TOAST_SUCCESS, TOAST_ERROR } = TOAST_TYPE;
const { EDIT, ADD, DESC, ASC } = GLOBAL;
const { BTN_ADD_HOLIDAY, BTN_SAVE } = BT_BUTTON_LABELS;
const { SR_HOLIDAY_NAME_COL } = LB_HOLIDAY_TABLE_COL_SORT;
const { LB_HOLIDAY_NAME, LB_HOLIDAY_DATE, LB_HOLIDAY_DESCRIPTION, LB_NATIONALITY } = INPUT_LABEL;
const { PH_HOLIDAY_NAME, PH_HOLIDAY_DATE, PH_HOLIDAY_DESCRIPTION, PH_NATIONALITY } = PLACEHOLDER;
const {
  LB_TBL_COL_ACTION,
  LB_TBL_COL_HOLIDAY_NAME,
  LB_TBL_COL_HOLIDAY_DATE,
  LB_TBL_COL_HOLIDAY_NATIONALITY,
  LB_TBL_COL_HOLIDAY_DESCRIPTION
} = LB_HOLIDAY_TABLE_COLS;
const { ADMIN, HR } = ROLES;

const holidayTableStyles = {
  ...sharedTableStyles,
  headCells: {
    style: {
      ...sharedTableStyles.headCells.style,
      color: '#334155',
      fontWeight: 700
    }
  },
  rows: {
    ...sharedTableStyles.rows,
    style: {
      ...sharedTableStyles.rows.style,
      color: '#6b7280'
    }
  },
  cells: {
    style: {
      ...sharedTableStyles.cells.style,
      color: '#6b7280',
      fontWeight: 600
    }
  }
};

const initialState = {
  isEditModalData: {},
  isModal: false,
  search: '',
  isDelete: { id: '', status: '' },
  currentPage: 1,
  rowsPerPage: 10,
  isNext: false,
  sortField: 'date',
  sortDirection: false,
  loading: false
};
function Holidays() {
  const [state, setState] = useState(initialState);

  const { data: authData } = useIsLoggedInQuery();
  const { data, isLoading, isFetching, refetch } = useGetHolidaysQuery({
    search: state.search,
    skip: skipRow(state.currentPage, state.rowsPerPage),
    limit: state.rowsPerPage,
    sort: state.sortField,
    sortType: state.sortDirection ? DESC : ASC
  });
  const [addHoliday] = useAddHolidayMutation();
  const [editHoliday] = useEditHolidayMutation();
  const [deleteHoliday] = useDeleteHolidayMutation();

  const handleChangeSort = (sortField) => {
    setState((prev) => ({
      ...prev,
      sortDirection: !prev.sortDirection,
      sortField: sortField.sortField
    }));
  };

  const defaultValue = {
    name: '',
    description: '',
    date: '',
    locality: []
  };

  const {
    register,
    reset,
    setValue,
    handleSubmit,
    control,
    formState: { errors }
  } = useForm({
    resolver: yupResolver(holidaySchema),
    values: state.isEditModalData || defaultValue
  });

  useEffect(() => {
    if (data?.isNext === undefined) return;
    setState((prevState) =>
      prevState.isNext === data.isNext ? prevState : { ...prevState, isNext: data.isNext }
    );
  }, [data?.isNext]);

  useEffect(() => {
    if (state.isEditModalData?._id) {
      const momentDate = editDateFormate(state?.isEditModalData.date);
      setValue('date', momentDate);
      setValue('locality', getRecordLocality(state.isEditModalData));
    }
  }, [state?.isEditModalData, setValue]);

  const EditHoliday = async (holidayData) => {
    try {
      setState({ ...state, loading: true });
      const res = await editHoliday({ data: holidayData, id: state?.isEditModalData._id });
      if (res?.data?.success) {
        refetch();
        reset();
        setState({ ...state, isModal: false, isEditModalData: {}, loading: false });
        notification(res?.data?.statusMessage, TOAST_SUCCESS);
      } else {
        reset();
        setState({ ...state, isModal: false, isEditModalData: {}, loading: false });
        notification(res?.data?.statusMessage, TOAST_ERROR);
      }
    } catch (error) {
      notification(error.message, TOAST_ERROR);
    }
  };

  const handleAddHoliday = async (holidayData) => {
    try {
      setState({ ...state, loading: true });
      const res = await addHoliday(holidayData);
      if (res?.data?.success) {
        refetch();
        reset();
        setState({ ...state, isModal: false, loading: false });
        notification(res?.data?.statusMessage, TOAST_SUCCESS);
      } else {
        reset();
        setState({ ...state, isModal: false, loading: false });
        notification(res?.data?.statusMessage, TOAST_ERROR);
      }
    } catch (error) {
      notification(error.message, TOAST_ERROR);
    }
  };

  const onSubmit = async (data) => {
    const start = new Date(data.date).getTime();
    const holidayData = {
      date: start,
      description: data.description,
      name: data.name,
      locality: data.locality
    };
    if (state?.isEditModalData._id) {
      EditHoliday(holidayData);
    } else {
      handleAddHoliday(holidayData);
    }
  };

  const handleDeleteHoliday = async (id) => {
    try {
      const res = await deleteHoliday(id);
      if (res?.data?.success) {
        refetch();
        setState({ ...state, isDelete: { id: '', status: false } });
        notification(res?.data?.statusMessage, TOAST_SUCCESS);
      } else {
        notification(res?.data?.statusMessage, TOAST_ERROR);
      }
    } catch (error) {
      notification(error.message, TOAST_ERROR);
    }
  };

  const roleName = authData?.data?.hrxRole?.roleName;
  const isHrOrAdmin = [ADMIN, HR].includes(roleName);

  const columnsT = [
    {
      name: LB_TBL_COL_HOLIDAY_NAME,
      sortField: SR_HOLIDAY_NAME_COL,
      selector: (row) => <span className="holiday-name">{row.name || ''}</span>,
      sortable: true
    },
    {
      name: LB_TBL_COL_HOLIDAY_DATE,
      selector: (row) => formatDate(row?.date),
      sortable: false
    },
    ...(isHrOrAdmin
      ? [
          {
            name: LB_TBL_COL_HOLIDAY_NATIONALITY,
            selector: (row) => (
              <span className={row.txtClass}>{getRecordLocality(row).join(', ') || '-'}</span>
            ),
            sortable: false
          }
        ]
      : []),
    {
      name: LB_TBL_COL_HOLIDAY_DESCRIPTION,
      selector: (row) => <span className={row.txtClass}>{row.description || ''}</span>,
      sortable: false
    }
  ];

  if (isHrOrAdmin) {
    columnsT.push({
      name: LB_TBL_COL_ACTION,
      selector: () => {},
      sortable: false,
      cell: (row) => (
        <RowActionMenu
          actions={[
            {
              label: 'Edit',
              icon: 'icofont-edit',
              variant: 'success',
              onClick: () => {
                setState({ ...state, isModal: true, isEditModalData: row });
              }
            },
            {
              label: 'Delete',
              icon: 'icofont-ui-delete',
              variant: 'danger',
              onClick: () => {
                setState({ ...state, isDelete: { id: row?._id, status: true } });
              }
            }
          ]}
        />
      )
    });
  }

  const handleChangePage = (page) => {
    setState((prev) => ({ ...prev, currentPage: page }));
  };

  const handleChangeRowsPerPage = (newRowsPerPage) => {
    setState((prev) => ({ ...prev, rowsPerPage: newRowsPerPage, currentPage: 1 }));
  };

  const debouncedOnSearchData = _debounce((value) => {
    setState((prev) => ({ ...prev, search: value, currentPage: 1 }));
  }, 300);

  return (
    <div className="container-xxl">
      <PageHeader headerTitle="Holidays" />
      <div className="row clearfix g-3">
        <div className="col-sm-12">
          <TableCard>
            <TableToolbar
              onSearch={debouncedOnSearchData}
              actions={
                isHrOrAdmin ? (
                  <button
                    type="button"
                    className="app-table-toolbar__add-btn"
                    onClick={() => {
                      reset(defaultValue);
                      setState({ ...state, isModal: true, isEditModalData: {} });
                    }}
                  >
                    <i className="icofont-plus-circle"></i>
                    {BTN_ADD_HOLIDAY}
                  </button>
                ) : null
              }
            />
            <div className="card-body">
              <div className="holiday-table">
                <DataTable
                  customStyles={holidayTableStyles}
                  title={HolidaysData.title}
                  columns={columnsT}
                  data={data?.data || []}
                  defaultSortField="title"
                  pagination
                  paginationServer
                  onSort={handleChangeSort}
                  sortField={state.sortField}
                  sortDirection={state.sortDirection}
                  paginationDefaultPage={state.currentPage}
                  paginationTotalRows={paginationRow(
                    data?.isNext,
                    state.rowsPerPage,
                    state.currentPage,
                    data?.data?.length,
                    data?.totalCount
                  )}
                  paginationPerPage={state.rowsPerPage}
                  paginationRowsPerPageOptions={[5, 10, 20, 30]}
                  paginationComponentOptions={{
                    noRowsPerPage: false,
                    rangeSeparatorText: 'of',
                    selectAllRowsItem: state.isNext,
                    selectAllRowsItemText: 'All'
                  }}
                  onChangePage={handleChangePage}
                  onChangeRowsPerPage={handleChangeRowsPerPage}
                  selectableRows={false}
                  noDataComponent={<EmptyData />}
                  className="table myDataTable table-hover align-middle mb-0 d-row nowrap dataTable no-footer dtr-inline"
                  highlightOnHover={true}
                  progressPending={isLoading || isFetching ? true : false}
                  progressComponent={<TableSkeleton columns={columnsT.length} />}
                />
              </div>
            </div>
          </TableCard>
        </div>
      </div>
      <DeleteModel
        isOpen={() => {
          setState({ ...state, isDelete: { id: '', status: false } });
        }}
        open={state?.isDelete?.status}
        title="Delete Holiday"
        message="Are you sure you want to Delete Holiday ?"
        isDelete={() => {
          handleDeleteHoliday(state?.isDelete.id);
        }}
      />
      <Modal
        centered
        className="holiday-modal"
        dialogClassName="holiday-modal__dialog"
        contentClassName="holiday-modal__content"
        show={state?.isModal}
        onHide={() => {
          reset(defaultValue);
          setState({ ...state, isModal: false, isEditModalData: '' });
        }}
      >
        <Modal.Header className="holiday-modal__header">
          <div className="holiday-modal__header-content">
            <span className="holiday-modal__header-icon" aria-hidden="true">
              <i className="icofont-calendar" />
            </span>
            <div className="holiday-modal__header-copy">
              <Modal.Title className="fw-bold holiday-modal__title">
                {state.isEditModalData?._id ? EDIT : ADD} {LB_HOLIDAY}
              </Modal.Title>
              <p className="holiday-modal__subtitle">
                {state.isEditModalData?._id
                  ? 'Update this holiday'
                  : 'Enter the holiday name, date, and locality'}
              </p>
            </div>
          </div>
          <button
            type="button"
            className="btn-close holiday-modal__close"
            aria-label="Close"
            onClick={() => {
              reset(defaultValue);
              setState({ ...state, isModal: false, isEditModalData: '' });
            }}
          />
        </Modal.Header>
        <form className="holiday-modal__form" onSubmit={handleSubmit(onSubmit)}>
          <Modal.Body className="holiday-modal__body">
            <div className="holiday-modal__section">
              <div className="holiday-modal__field">
                <label className="holiday-modal__label" htmlFor="exampleFormControlInput1">
                  {LB_HOLIDAY_NAME}
                </label>
                <input
                  type="text"
                  className="form-control holiday-modal__input"
                  {...register('name')}
                  id="exampleFormControlInput1"
                  placeholder={PH_HOLIDAY_NAME}
                  onClick={() => {}}
                />
                <small className="text-danger holiday-modal__error">
                  {getErrorMessage('name', errors)}
                </small>
              </div>
              <div className="holiday-modal__field">
                <label className="holiday-modal__label" htmlFor="exampleFormControlInput2778">
                  {LB_HOLIDAY_DATE}
                </label>
                <input
                  type="date"
                  className="form-control holiday-modal__input"
                  {...register('date')}
                  placeholder={PH_HOLIDAY_DATE}
                  id="exampleFormControlInput2778"
                  onClick={() => {}}
                />
                <small className="text-danger holiday-modal__error">
                  {getErrorMessage('date', errors)}
                </small>
              </div>
              <div className="holiday-modal__field">
                <label className="holiday-modal__label">{LB_NATIONALITY}</label>
                <Controller
                  name="locality"
                  control={control}
                  render={({ field }) => (
                    <Select
                      className="holiday-modal__select"
                      classNamePrefix="holiday-modal-select"
                      menuPortalTarget={typeof document !== 'undefined' ? document.body : null}
                      menuPosition="fixed"
                      styles={{ menuPortal: (base) => ({ ...base, zIndex: 100000 }) }}
                      isMulti
                      placeholder={PH_NATIONALITY}
                      options={HOLIDAY_LOCALITY_OPTIONS}
                      value={HOLIDAY_LOCALITY_OPTIONS.filter((opt) =>
                        (field.value || []).includes(opt.value)
                      )}
                      onChange={(selected) =>
                        field.onChange(selected ? selected.map((item) => item.value) : [])
                      }
                    />
                  )}
                />
                <small className="text-danger holiday-modal__error">
                  {getErrorMessage('locality', errors)}
                </small>
              </div>
              <div className="holiday-modal__field">
                <label className="holiday-modal__label" htmlFor="holiday-description">
                  {LB_HOLIDAY_DESCRIPTION}
                </label>
                <textarea
                  className="form-control holiday-modal__textarea"
                  {...register('description')}
                  placeholder={PH_HOLIDAY_DESCRIPTION}
                  id="holiday-description"
                  onClick={() => {}}
                />
                <small className="text-danger holiday-modal__error">
                  {getErrorMessage('description', errors)}
                </small>
              </div>
            </div>
          </Modal.Body>
          <Modal.Footer className="holiday-modal__footer">
            <button type="submit" disabled={state.loading} className="btn holiday-modal__submit">
              {state.loading ? (
                <div className="spinner-border spinner-border-sm" role="status"></div>
              ) : (
                <>
                  <i className="icofont-check-circled" aria-hidden="true" />
                  {BTN_SAVE}
                </>
              )}
            </button>
          </Modal.Footer>
        </form>
      </Modal>
    </div>
  );
}

export default Holidays;
